import { Button, Select, SelectItem } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';
import { openFolderPicker } from '../../../lib/googlePicker';

interface DriveFolder {
  id: string;
  remoteId?: string;
  name: string;
}

interface DriveFolderFieldProps {
  /** Connected Drive credential uuid the folder list + Picker operate against. */
  integrationUuid: string;
  /** Selected folder id ('' = none). */
  value: string;
  onChange: (folderId: string) => void;
  disabled?: boolean;
  label?: string;
  /** Hint shown under the field (e.g. the per-customer subfolder note). */
  hint?: React.ReactNode;
  /**
   * Notified with the resolved NAME of the selected folder whenever it changes (including after
   * the folder list first loads). Lets a parent show the folder name elsewhere (e.g. an
   * ExportSubfolderField's "nested inside {name}" hint) without owning the folder list.
   */
  onSelectedFolderNameChange?: (name: string) => void;
  /**
   * Compact layout: the dropdown and Browse button share one row, and the "create new folder"
   * affordance is hidden behind a "+ New folder" reveal instead of always showing. Used where the
   * picker would otherwise dominate (e.g. the Global Settings export card).
   */
  compact?: boolean;
}

const folderKey = (f: DriveFolder) => f.id ?? f.remoteId ?? '';

/**
 * DriveFolderField - design-aligned selector for a Google Drive destination folder.
 *
 * Consolidates the folder-selection UX that was duplicated across every export-destination
 * section: a dropdown of known folders, a "Browse Google Drive" button that opens the Picker
 * (the only `drive.file`-compatible way to grant access to an existing folder), and an inline
 * "create folder" affordance. Under `drive.file` the folder list only contains app-created /
 * Picker-granted folders, so a fresh credential starts empty until the user picks or creates one.
 *
 * The parent owns only the folderId (value/onChange); all folder-list / picker / create state
 * lives here, so every section stays in lock-step from one place.
 */
export function DriveFolderField({
  integrationUuid,
  value,
  onChange,
  disabled,
  label = 'Folder',
  hint,
  onSelectedFolderNameChange,
  compact = false,
}: DriveFolderFieldProps) {
  const [folders, setFolders] = React.useState<DriveFolder[]>([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [isOpeningPicker, setIsOpeningPicker] = React.useState(false);
  const [pickerError, setPickerError] = React.useState<string | null>(null);
  const [pickerUnavailable, setPickerUnavailable] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  // In compact mode the create-folder row is collapsed behind a "+ New folder" reveal.
  const [showCreate, setShowCreate] = React.useState(false);

  React.useEffect(() => {
    if (!integrationUuid) {
      setFolders([]);
      return;
    }
    let active = true;
    setIsFetching(true);
    setHasFetched(false);
    apiFetch<{ folders: DriveFolder[] }>(`/api/integration/${integrationUuid}/folders`)
      .then((data) => {
        if (active) setFolders(data.folders);
      })
      .catch(() => {
        // Folder listing is best-effort: the Picker + create still work without it.
      })
      .finally(() => {
        if (active) {
          setIsFetching(false);
          setHasFetched(true);
        }
      });
    return () => {
      active = false;
    };
  }, [integrationUuid]);

  const handleOpenPicker = async () => {
    if (!integrationUuid) return;
    setPickerError(null);
    try {
      setIsOpeningPicker(true);
      const cfg = await apiFetch<{
        accessToken: string;
        apiKey: string | null;
        appId: string | null;
      }>(`/api/integration/${integrationUuid}/picker-config`);
      if (!cfg.apiKey) {
        // Picker key not provisioned for this workspace - degrade to create-only.
        setPickerUnavailable(true);
        return;
      }
      const picked = await openFolderPicker({
        accessToken: cfg.accessToken,
        apiKey: cfg.apiKey,
        appId: cfg.appId,
      });
      if (picked) {
        setFolders((prev) =>
          prev.some((f) => folderKey(f) === picked.id)
            ? prev
            : [...prev, { id: picked.id, name: picked.name }].sort((a, b) =>
                a.name.localeCompare(b.name)
              )
        );
        onChange(picked.id);
      }
    } catch (e) {
      console.error('[DriveFolderField] open picker failed', e);
      setPickerError('Could not open the Google Picker. Please try again.');
    } finally {
      setIsOpeningPicker(false);
    }
  };

  // Surface the resolved name of the current selection so a parent can reference it (e.g. a
  // subfolder hint) without holding the folder list itself.
  const selectedName = folders.find((f) => folderKey(f) === value)?.name ?? '';
  React.useEffect(() => {
    onSelectedFolderNameChange?.(selectedName);
  }, [selectedName, onSelectedFolderNameChange]);

  const duplicateName = !!(
    newFolderName.trim() &&
    folders.some((f) => f.name.toLowerCase() === newFolderName.trim().toLowerCase())
  );

  const handleCreateFolder = async () => {
    if (!integrationUuid || !newFolderName.trim() || duplicateName) return;
    try {
      setIsCreating(true);
      const { folder } = await apiFetch<{ folder: DriveFolder }>(
        `/api/integration/${integrationUuid}/folders`,
        { method: 'POST', body: JSON.stringify({ name: newFolderName.trim() }) }
      );
      setFolders((prev) => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(folder.id);
      setNewFolderName('');
    } catch (e) {
      console.error('[DriveFolderField] create folder failed', e);
    } finally {
      setIsCreating(false);
    }
  };

  const selectEl = (
    <Select
      value={value}
      onValueChange={onChange}
      size="comfortable"
      disabled={disabled || isFetching || folders.length === 0}
      loading={isFetching}
    >
      {folders.map((f) => (
        <SelectItem key={folderKey(f)} value={folderKey(f)}>
          {f.name}
        </SelectItem>
      ))}
    </Select>
  );

  const browseButton = !pickerUnavailable && (
    <Button
      variant="secondary"
      onClick={() => void handleOpenPicker()}
      disabled={disabled || isOpeningPicker}
    >
      {isOpeningPicker ? 'Opening…' : compact ? 'Browse…' : 'Browse Google Drive…'}
    </Button>
  );

  const createRow = (
    <div className="ptp-est-range-config__create-row">
      <div className="ptp-est-range-config__create-input-group">
        <input
          className={`ptp-est-range-config__create-input${
            duplicateName ? ' ptp-est-range-config__create-input--error' : ''
          }`}
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder={compact ? 'New folder name...' : 'Or create a new folder...'}
          disabled={disabled}
        />
        {duplicateName && (
          <span className="ptp-est-range-config__block-error">
            A folder with this name already exists.
          </span>
        )}
      </div>
      <Button
        variant="secondary"
        onClick={() => void handleCreateFolder()}
        disabled={disabled || isCreating || !newFolderName.trim() || duplicateName}
      >
        Create
      </Button>
    </div>
  );

  return (
    <div className={`drive-folder-field${compact ? ' drive-folder-field--compact' : ''}`}>
      <label className="ptp-est-range-config__block-label">{label}</label>
      {compact ? (
        <div className="drive-folder-field__picker-row">
          {selectEl}
          {browseButton}
        </div>
      ) : (
        <>
          {selectEl}
          {browseButton}
        </>
      )}
      {pickerError && <span className="ptp-est-range-config__block-error">{pickerError}</span>}
      {hasFetched && folders.length === 0 && !isFetching && (
        <span className="ptp-est-range-config__block-hint">
          {pickerUnavailable
            ? 'Create a new folder to use as the destination.'
            : 'No folders yet - use “Browse” to pick an existing folder, or create a new one.'}
        </span>
      )}
      {/* In compact mode the create-folder row hides behind a "+ New folder" reveal. */}
      {!compact || showCreate ? (
        createRow
      ) : (
        <button
          type="button"
          className="drive-folder-field__new-link"
          onClick={() => setShowCreate(true)}
          disabled={disabled}
        >
          + New folder
        </button>
      )}
      {hint ? <div className="ptp-est-range-config__block-hint">{hint}</div> : null}
    </div>
  );
}
